import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, Monitor, Tablet, Wifi, Battery, Signal, Clock, MapPin, CheckCircle, Bell, DollarSign, Camera, Shield, FileText, Users, Navigation, Star, ChevronRight, Home, BarChart2, Package, Wrench, MessageSquare, QrCode } from 'lucide-react';

type AppType = 'owner' | 'technician' | 'customer';

const apps: { id: AppType; label: string; desc: string; color: string; gradient: string }[] = [
  { id: 'owner', label: 'Owner App', desc: 'Business control at your fingertips', color: 'from-violet-500 to-indigo-600', gradient: 'from-violet-50 to-indigo-50' },
  { id: 'technician', label: 'Technician App', desc: 'Field operations & job management', color: 'from-blue-500 to-cyan-600', gradient: 'from-blue-50 to-cyan-50' },
  { id: 'customer', label: 'Customer App', desc: 'Warranty, invoices & support', color: 'from-green-500 to-emerald-600', gradient: 'from-green-50 to-emerald-50' },
];

function OwnerScreen({ screen }: { screen: string }) {
  if (screen === 'dashboard') return (
    <div className="p-3 space-y-3">
      <div className="text-xs font-semibold text-gray-700 mb-1">Good Morning, Owner 👋</div>
      {[['Revenue', 'LKR 4.2M', '▲ 10.5%', 'violet'], ['Projects', '18', '▲ 3', 'blue'], ['Customers', '284', '▲ 12', 'green'], ['Profit', 'LKR 1.68M', '▲ 10.5%', 'amber']].map(([label, val, change, color]) => (
        <div key={label as string} className={`bg-${color}-50 rounded-xl p-2.5 border border-${color}-100`}>
          <div className="text-[10px] text-gray-500">{label}</div>
          <div className="text-sm font-bold text-gray-900">{val}</div>
          <div className="text-[10px] text-green-600">{change}</div>
        </div>
      ))}
    </div>
  );
  if (screen === 'finance') return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold text-gray-700">Finance Overview</div>
      <div className="bg-violet-500 rounded-xl p-3 text-white">
        <div className="text-[10px] opacity-80">Total Balance</div>
        <div className="text-base font-bold">LKR 8,450,000</div>
      </div>
      {[['Operations Account', 'LKR 3.2M'], ['Salary Account', 'LKR 890K'], ['Tax Reserve', 'LKR 420K'], ['Emergency', 'LKR 1.2M']].map(([name, amount]) => (
        <div key={name as string} className="flex justify-between text-xs p-2 bg-gray-50 rounded-lg">
          <span className="text-gray-600">{name}</span>
          <span className="font-semibold text-gray-900">{amount}</span>
        </div>
      ))}
    </div>
  );
  return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold text-gray-700">AI Assistant</div>
      <div className="bg-violet-50 rounded-xl p-2.5 text-xs text-gray-700">
        <div className="flex items-center gap-1 mb-1.5">
          <Star className="w-3 h-3 text-violet-500" />
          <span className="font-medium text-violet-700">AI Insight</span>
        </div>
        Starlink demand up 340%. Recommend restocking 5 units this week.
      </div>
      <div className="bg-amber-50 rounded-xl p-2.5 text-xs text-gray-700">
        <div className="flex items-center gap-1 mb-1.5">
          <Bell className="w-3 h-3 text-amber-500" />
          <span className="font-medium text-amber-700">Alert</span>
        </div>
        3 invoices overdue. Total: LKR 125,000.
      </div>
    </div>
  );
}

function TechnicianScreen({ screen }: { screen: string }) {
  if (screen === 'jobs') return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold text-gray-700">Today's Jobs (4)</div>
      {[
        { id: 'J001', customer: 'AIA Insurance', service: 'CCTV Install', time: '9:00 AM', status: 'in-progress', color: 'bg-green-100 text-green-700' },
        { id: 'J002', customer: 'Hotel Reef', service: 'Network Setup', time: '11:00 AM', status: 'pending', color: 'bg-amber-100 text-amber-700' },
        { id: 'J003', customer: 'Lanka Hospitals', service: 'Alarm Maint.', time: '2:00 PM', status: 'pending', color: 'bg-amber-100 text-amber-700' },
      ].map(job => (
        <div key={job.id} className="bg-white rounded-xl border border-gray-100 p-2.5">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[10px] text-gray-500">{job.time}</div>
              <div className="text-xs font-semibold text-gray-900">{job.customer}</div>
              <div className="text-[10px] text-gray-500">{job.service}</div>
            </div>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${job.color}`}>{job.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
  if (screen === 'checkin') return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold text-gray-700">Job Check-In</div>
      <div className="bg-blue-500 rounded-xl p-3 text-white text-center">
        <MapPin className="w-5 h-5 mx-auto mb-1" />
        <div className="text-xs font-medium">AIA Insurance, Colombo 3</div>
        <div className="text-[10px] opacity-80 mt-0.5">Tap to confirm location</div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-2.5">
        <div className="text-[10px] text-gray-500 mb-2">Installation Checklist</div>
        {['Materials verified', 'Site surveyed', 'Power confirmed', 'Cable route planned'].map((item, i) => (
          <div key={i} className="flex items-center gap-2 py-1">
            <div className={`w-3.5 h-3.5 rounded-full border ${i < 2 ? 'bg-green-500 border-green-500' : 'border-gray-300'} flex items-center justify-center`}>
              {i < 2 && <CheckCircle className="w-2.5 h-2.5 text-white" />}
            </div>
            <span className="text-[10px] text-gray-600">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold text-gray-700">Upload Photos</div>
      <div className="grid grid-cols-2 gap-1.5">
        {['Before 1', 'Before 2', 'After 1', 'After 2'].map((label, i) => (
          <div key={label} className={`aspect-square rounded-xl flex flex-col items-center justify-center ${i < 2 ? 'bg-gray-800' : 'bg-gray-100 border-2 border-dashed border-gray-300'}`}>
            {i < 2 ? <Camera className="w-4 h-4 text-white/60" /> : <Camera className="w-4 h-4 text-gray-400" />}
            <span className={`text-[9px] mt-0.5 ${i < 2 ? 'text-white/60' : 'text-gray-400'}`}>{label}</span>
          </div>
        ))}
      </div>
      <button className="w-full bg-blue-500 text-white text-xs font-medium py-2 rounded-xl">
        Generate AI Report
      </button>
    </div>
  );
}

function CustomerScreen({ screen }: { screen: string }) {
  if (screen === 'warranty') return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold text-gray-700">My Warranties</div>
      {[
        { product: 'Hikvision CCTV System', expires: '2028-03-15', status: 'Active', daysLeft: 614 },
        { product: 'Paradox Alarm System', expires: '2027-06-20', status: 'Active', daysLeft: 345 },
        { product: 'Dahua NVR', expires: '2026-09-10', status: 'Expiring', daysLeft: 62 },
      ].map((w, i) => (
        <div key={i} className={`rounded-xl p-2.5 border ${w.status === 'Expiring' ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
          <div className="text-xs font-semibold text-gray-900">{w.product}</div>
          <div className="flex justify-between mt-0.5">
            <span className="text-[10px] text-gray-500">Expires: {w.expires}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${w.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{w.status}</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">{w.daysLeft} days remaining</div>
        </div>
      ))}
    </div>
  );
  if (screen === 'payments') return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold text-gray-700">Invoices & Payments</div>
      {[
        { id: 'INV-047', amount: 'LKR 485,000', status: 'Paid', date: 'Jul 8' },
        { id: 'INV-051', amount: 'LKR 85,000', status: 'Pending', date: 'Jul 12' },
        { id: 'INV-053', amount: 'LKR 42,500', status: 'Draft', date: 'Jul 10' },
      ].map((inv, i) => (
        <div key={i} className="flex items-center gap-2.5 bg-white border border-gray-100 rounded-xl p-2.5">
          <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${inv.status === 'Paid' ? 'bg-green-100' : 'bg-amber-100'}`}>
            <DollarSign className={`w-3.5 h-3.5 ${inv.status === 'Paid' ? 'text-green-600' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1">
            <div className="text-xs font-semibold text-gray-900">{inv.id}</div>
            <div className="text-[10px] text-gray-500">{inv.amount}</div>
          </div>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${inv.status === 'Paid' ? 'bg-green-100 text-green-700' : inv.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{inv.status}</span>
        </div>
      ))}
    </div>
  );
  return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold text-gray-700">Track Technician</div>
      <div className="bg-green-50 rounded-xl p-3 border border-green-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
            <Navigation className="w-3 h-3 text-white" />
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-900">Kamal Perera</div>
            <div className="text-[10px] text-green-600 animate-pulse">● En-route to you</div>
          </div>
        </div>
        <div className="mt-2 bg-white rounded-lg p-2 text-center">
          <div className="text-sm font-bold text-green-600">14 min</div>
          <div className="text-[10px] text-gray-500">Estimated arrival</div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-100 p-2.5">
        <div className="text-[10px] text-gray-500 mb-1.5">Support Chat</div>
        <div className="bg-gray-100 rounded-lg px-2.5 py-1.5 text-[10px] text-gray-600 mb-1">
          Hi! The technician is on the way. ETA 14 min.
        </div>
        <div className="bg-green-500 rounded-lg px-2.5 py-1.5 text-[10px] text-white ml-4">
          Thank you! I'm ready.
        </div>
      </div>
    </div>
  );
}

function PhoneMockup({ app, screen, color }: { app: AppType; screen: string; color: string }) {
  const screens = {
    owner: ['dashboard', 'finance', 'ai'],
    technician: ['jobs', 'checkin', 'photos'],
    customer: ['warranty', 'payments', 'track'],
  };
  const screenLabels: Record<string, Record<string, string>> = {
    owner: { dashboard: 'Dashboard', finance: 'Finance', ai: 'AI Assistant' },
    technician: { jobs: "Today's Jobs", checkin: 'Check-In', photos: 'Photos' },
    customer: { warranty: 'Warranty', payments: 'Invoices', track: 'Track' },
  };

  return (
    <div className="flex flex-col items-center">
      {/* Phone frame */}
      <div className="relative w-48 h-96 bg-gray-900 rounded-[2.5rem] shadow-2xl border-4 border-gray-800 overflow-hidden">
        {/* Notch */}
        <div className="absolute top-0 left-0 right-0 h-7 bg-gray-900 z-10 flex items-center justify-between px-4 pt-1">
          <span className="text-[8px] text-white font-medium">9:41</span>
          <div className="flex items-center gap-1">
            <Signal className="w-2.5 h-2.5 text-white" />
            <Wifi className="w-2.5 h-2.5 text-white" />
            <Battery className="w-2.5 h-2.5 text-white" />
          </div>
        </div>
        {/* Screen */}
        <div className="absolute inset-0 top-7 bg-gray-50 overflow-y-auto">
          {/* App header */}
          <div className={`bg-gradient-to-r ${color} px-3 py-2.5`}>
            <div className="text-[10px] text-white/80">K-Tec</div>
            <div className="text-xs font-bold text-white">{screenLabels[app][screen]}</div>
          </div>
          {/* Content */}
          {app === 'owner' && <OwnerScreen screen={screen} />}
          {app === 'technician' && <TechnicianScreen screen={screen} />}
          {app === 'customer' && <CustomerScreen screen={screen} />}
        </div>
        {/* Home bar */}
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-16 h-1 bg-gray-600 rounded-full" />
      </div>
    </div>
  );
}

export default function MobileAppsPage() {
  const [activeApp, setActiveApp] = useState<AppType>('owner');
  const [activeScreen, setActiveScreen] = useState('dashboard');

  const screens: Record<AppType, { id: string; label: string; icon: typeof Home }[]> = {
    owner: [
      { id: 'dashboard', label: 'Dashboard', icon: Home },
      { id: 'finance', label: 'Finance', icon: DollarSign },
      { id: 'ai', label: 'AI Assistant', icon: Star },
    ],
    technician: [
      { id: 'jobs', label: "Today's Jobs", icon: Wrench },
      { id: 'checkin', label: 'Check-In', icon: MapPin },
      { id: 'photos', label: 'Photos', icon: Camera },
    ],
    customer: [
      { id: 'warranty', label: 'Warranty', icon: Shield },
      { id: 'payments', label: 'Invoices', icon: FileText },
      { id: 'track', label: 'Track Tech', icon: Navigation },
    ],
  };

  const currentApp = apps.find(a => a.id === activeApp)!;

  const handleAppChange = (appId: AppType) => {
    setActiveApp(appId);
    setActiveScreen(screens[appId][0].id);
  };

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-200">
          <Smartphone className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Mobile Applications</h1>
          <p className="text-xs text-gray-500">Owner · Technician · Customer — Three dedicated apps</p>
        </div>
      </div>

      {/* App Selector */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {apps.map(app => (
          <button
            key={app.id}
            onClick={() => handleAppChange(app.id)}
            className={`p-5 rounded-2xl border-2 transition-all text-left ${activeApp === app.id ? `border-transparent bg-gradient-to-br ${app.gradient} shadow-sm` : 'border-gray-200 bg-white hover:border-gray-300'}`}
          >
            <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${app.color} flex items-center justify-center mb-3 shadow-sm`}>
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">{app.label}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{app.desc}</p>
            {activeApp === app.id && (
              <div className="flex items-center gap-1 mt-2 text-xs font-medium text-violet-600">
                Previewing <ChevronRight className="w-3 h-3" />
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="flex gap-8 justify-center">
        {/* Screen Selector */}
        <div className="flex flex-col gap-2 pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Screens</p>
          {screens[activeApp].map(screen => {
            const Icon = screen.icon;
            return (
              <button
                key={screen.id}
                onClick={() => setActiveScreen(screen.id)}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm transition-all ${activeScreen === screen.id ? `bg-gradient-to-r ${currentApp.color} text-white shadow-sm` : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              >
                <Icon className="w-4 h-4" />
                {screen.label}
              </button>
            );
          })}
        </div>

        {/* Phone Preview */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeApp}-${activeScreen}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <PhoneMockup app={activeApp} screen={activeScreen} color={currentApp.color} />
          </motion.div>
        </AnimatePresence>

        {/* App Features */}
        <div className="flex flex-col gap-2 pt-4 max-w-xs">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Features</p>
          {activeApp === 'owner' && ['Dashboard with real-time KPIs', 'Revenue & profit tracking', 'Customer & project overview', 'Finance management', 'AI Assistant & insights', 'Push notifications', 'Inventory overview', 'Staff management'].map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
              <CheckCircle className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" /> {f}
            </div>
          ))}
          {activeApp === 'technician' && ["Today's job schedule", 'GPS navigation to site', 'Check-in with location', 'Before/after photo upload', 'Installation checklist', 'Customer signature capture', 'Completion report generation', 'Warranty activation', 'Offline mode support'].map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
              <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" /> {f}
            </div>
          ))}
          {activeApp === 'customer' && ['Live warranty status', 'Project progress tracking', 'Invoice & payment history', 'Online payment gateway', 'Service request submission', 'WhatsApp support chat', 'Real-time tech tracking', 'Document downloads (PDF)'].map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /> {f}
            </div>
          ))}
        </div>
      </div>

      {/* Coming Soon Banner */}
      <div className="mt-8 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Mobile Apps — Coming Soon</h3>
            <p className="text-xs text-gray-400 mt-1">React Native apps for iOS & Android with offline mode, push notifications, and biometric auth.</p>
          </div>
          <div className="flex gap-3">
            <div className="bg-white/10 rounded-xl px-4 py-2 text-xs text-center">
              <div className="font-semibold">iOS</div>
              <div className="text-gray-400">App Store</div>
            </div>
            <div className="bg-white/10 rounded-xl px-4 py-2 text-xs text-center">
              <div className="font-semibold">Android</div>
              <div className="text-gray-400">Play Store</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
